import {
  AppScopeIdentity,
  AppScopeIdentityAt
} from 'v5/core/app-scope/appScope.types';
import {
  settingCatalog,
  SettingDefinedAt,
  SettingName,
  SettingValue,
  TeamSettingName,
  WorkspaceSettingName
} from 'v5/api/system-settings/settingCatalog';
import {
  ResolvedSetting,
  SystemSetting,
  SystemSettingOrigin
} from 'v5/api/system-settings/system-settings.api';
import { SystemSettingWireRow } from 'store/v1/auth/systemSettings.bridge';

export type SystemSettingsRows = SystemSetting[] | SystemSettingWireRow[];

function rowMatchesScope(
  row: SystemSettingWireRow | SystemSetting,
  workspaceId: number | null,
  teamId: number | null
): boolean {
  return (
    (row.enterprise_workspace_id ?? null) === workspaceId &&
    (row.enterprise_team_id ?? null) === teamId
  );
}

export function lensForSetting(
  scope: AppScopeIdentity,
  definedAt: 'workspace' | 'team'
): { workspaceId: number | null; teamId: number | null } {
  if (definedAt === 'workspace') {
    // Org browsing → global row only (send dialog / org URL).
    if (scope.level === 'organization') {
      return { workspaceId: null, teamId: null };
    }
    return { workspaceId: scope.workspaceId, teamId: null };
  }
  if (scope.level !== 'team') {
    throw new Error('team-defined setting requires team scope');
  }
  return { workspaceId: scope.workspaceId, teamId: scope.teamId };
}

export function assertWriteScope<K extends SettingName>(
  scope: AppScopeIdentity,
  name: K
): asserts scope is AppScopeIdentityAt<SettingDefinedAt<K>> {
  const definedAt = settingCatalog[name].definedAt;
  if (scope.level !== definedAt) {
    throw new Error(
      `Cannot write ${name} (definedAt: ${definedAt}) from ${scope.level} scope`
    );
  }
}

export function resolveSettingValue<K extends SettingName>(
  rows: SystemSettingsRows,
  scope: AppScopeIdentity,
  name: K
): ResolvedSetting<SettingValue<K>> {
  const definedAt = settingCatalog[name].definedAt;
  const lens = lensForSetting(scope, definedAt);
  const scoped = rows.find(
    row =>
      row.name === name && rowMatchesScope(row, lens.workspaceId, lens.teamId)
  );
  if (scoped) {
    const isGlobalRow =
      (scoped.enterprise_workspace_id ?? null) === null &&
      (scoped.enterprise_team_id ?? null) === null;
    return {
      name,
      value: scoped.value as SettingValue<K>,
      origin: isGlobalRow ? 'global' : definedAt
    };
  }

  const global = rows.find(
    row =>
      row.name === name &&
      row.enterprise_workspace_id == null &&
      row.enterprise_team_id == null
  );
  if (global) {
    return {
      name,
      value: global.value as SettingValue<K>,
      origin: 'global'
    };
  }

  return {
    name,
    value: settingCatalog[name].default as SettingValue<K>,
    origin: 'default' satisfies SystemSettingOrigin
  };
}

export function resolveAllSettings(
  rows: SystemSettingsRows,
  scope: AppScopeIdentity
): Record<string, ResolvedSetting> {
  const result: Record<string, ResolvedSetting> = {};
  for (const name of Object.keys(settingCatalog) as SettingName[]) {
    try {
      result[name] = resolveSettingValue(rows, scope, name);
    } catch {
      // Skip keys that cannot be resolved from this browsing scope (e.g. team key at workspace)
    }
  }
  return result;
}

export type WriteScopeFor<K extends SettingName> = AppScopeIdentityAt<
  SettingDefinedAt<K>
>;

/** Workspace-defined keys: org reads global; workspace/team apply override lens. */
export type ReadScopeForWorkspaceKey = AppScopeIdentity;
export type ReadScopeForTeamKey = AppScopeIdentityAt<'team'>;

export type { WorkspaceSettingName, TeamSettingName };
